import { Text, Stack, FormControl, InputGroup, Input } from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue, Cell } from 'react-table';

import Button from 'components/Button';
import Table from 'components/Table';
import Title from 'components/Title';
import {
  ISandataIndianaTasksSearchQuery,
  useFetchSandataIndianaTasks,
} from 'hooks/integrations/sandataIndiana/useFetchSandataIndianaTasks';
import { useMapTaskToProcedureCode } from 'hooks/integrations/sandataIndiana/useMapTasksToProcedureCode';

const TaskMapping: React.FC<unknown> = () => {
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [searchQuery, setSearchQuery] = useState<ISandataIndianaTasksSearchQuery>({
    page: 1,
    page_size: 25,
  });

  const { t } = useTranslation(['common', 'integrations', 'employees', 'jobsTasks']);

  const { data, status, refetch, isFetching } = useFetchSandataIndianaTasks(searchQuery);
  const { status: mapTaskToProcedureCodeStatus, mutateAsync: mapTaskToProcedureCodeMutateAsync } =
    useMapTaskToProcedureCode();

  const columns = [
    {
      Header: t('common:name'),
      accessor: 'name',
      Cell: (data: CellValue) => (
        <Stack direction="column" spacing="0.5">
          <Text fontWeight="semibold" color="primary.500">
            {data.value || ''}
          </Text>
        </Stack>
      ),
    },
    {
      Header: t('integrations:sandataIndiana.taskStatus'),
      accessor: 'task_status',
      Cell: (data: CellValue) => <Text>{data.value ? 'Active' : 'Inactive'}</Text>,
    },
    {
      Header: t('common:description'),
      accessor: 'description',
      Cell: (data: CellValue) => <Text>{data.value || '-'}</Text>,
    },

    {
      Header: t('integrations:sandataIndiana.procedureCode'),
      accessor: 'procedure_code',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <FormControl>
            <InputGroup size="lg">
              <Input
                id={`input-procedureCode-${data.row.original.id}`}
                defaultValue={data.value}
                fontSize="sm"
                variant="filled"
                type="text"
                border="1px"
                borderColor="inherit"
                isDisabled={isFetching || mapTaskToProcedureCodeStatus === 'loading'}
                placeholder="Enter procedure code"
              />
            </InputGroup>
          </FormControl>
        );
      },
    },
    {
      Header: t('integrations:sandataIndiana.modifier1'),
      accessor: 'modifier_1',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <FormControl>
            <InputGroup size="lg">
              <Input
                id={`input-modifier1-${data.row.original.id}`}
                defaultValue={data.value}
                fontSize="sm"
                variant="filled"
                type="text"
                border="1px"
                borderColor="inherit"
                isDisabled={isFetching || mapTaskToProcedureCodeStatus === 'loading'}
                placeholder="Enter modifier 1"
              />
            </InputGroup>
          </FormControl>
        );
      },
    },
    {
      Header: t('integrations:sandataIndiana.modifier2'),
      accessor: 'modifier_2',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <FormControl>
            <InputGroup size="lg">
              <Input
                id={`input-modifier2-${data.row.original.id}`}
                defaultValue={data.value}
                fontSize="sm"
                variant="filled"
                type="text"
                border="1px"
                borderColor="inherit"
                isDisabled={isFetching || mapTaskToProcedureCodeStatus === 'loading'}
                placeholder="Enter modifier 2"
              />
            </InputGroup>
          </FormControl>
        );
      },
    },
    {
      Header: t('integrations:sandataIndiana.modifier3'),
      accessor: 'modifier_3',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <FormControl>
            <InputGroup size="lg">
              <Input
                id={`input-modifier3-${data.row.original.id}`}
                defaultValue={data.value}
                fontSize="sm"
                variant="filled"
                type="text"
                border="1px"
                borderColor="inherit"
                isDisabled={isFetching || mapTaskToProcedureCodeStatus === 'loading'}
                placeholder="Enter modifier 3"
              />
            </InputGroup>
          </FormControl>
        );
      },
    },
    {
      Header: t('integrations:sandataIndiana.modifier4'),
      accessor: 'modifier_4',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <FormControl>
            <InputGroup size="lg">
              <Input
                id={`input-modifier4-${data.row.original.id}`}
                defaultValue={data.value}
                fontSize="sm"
                variant="filled"
                type="text"
                border="1px"
                borderColor="inherit"
                isDisabled={isFetching || mapTaskToProcedureCodeStatus === 'loading'}
                placeholder="Enter modifier 4"
              />
            </InputGroup>
          </FormControl>
        );
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 180,
      Cell: (data: Cell<any>) => {
        return (
          <Button
            bg="rgba(238, 112, 56, 0.1)"
            _hover={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            _active={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            color="secondary.300"
            isLoading={activeTaskId === data.row.original.id && mapTaskToProcedureCodeStatus === 'loading'}
            onClick={() => handleMapTask(data.row.original)}
          >
            {t('integrations:sandataIndiana.mapTask')}
          </Button>
        );
      },
    },
  ];

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: ISandataIndianaTasksSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handleMapTask = (rowData: any) => {
    const input = document.getElementById(`input-procedureCode-${rowData.id}`) as HTMLInputElement;
    const modifier1 = document.getElementById(`input-modifier1-${rowData.id}`) as HTMLInputElement;
    const modifier2 = document.getElementById(`input-modifier2-${rowData.id}`) as HTMLInputElement;
    const modifier3 = document.getElementById(`input-modifier3-${rowData.id}`) as HTMLInputElement;
    const modifier4 = document.getElementById(`input-modifier4-${rowData.id}`) as HTMLInputElement;

    if (input) {
      const postData = {
        task_id: rowData.id,
        procedure_code: input.value,
        modifier_1: modifier1?.value || '',
        modifier_2: modifier2?.value || '',
        modifier_3: modifier3?.value || '',
        modifier_4: modifier4?.value || '',
      };

      setActiveTaskId(rowData.id);

      mapTaskToProcedureCodeMutateAsync(postData).then(() => {
        refetch();
        setActiveTaskId(null);
      });
    }
  };

  return (
    <>
      <Title>{t('integrations:sandataIndiana.sandataIndianaTaskMapping')}</Title>
      <Table
        columns={columns}
        data={data?.payload || []}
        paginationData={data?.meta_data?.pagination}
        isLoading={status === 'loading'}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default TaskMapping;
